import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Activity, Heart, AlertTriangle, TrendingUp, Plus, CheckCircle2, User, BarChart3, Shield, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const RANGES = { systolic_bp: { label: 'Systolic BP', unit: 'mmHg', min: 90, max: 140 }, diastolic_bp: { label: 'Diastolic BP', unit: 'mmHg', min: 60, max: 90 }, pulse: { label: 'Pulse', unit: 'bpm', min: 60, max: 100 }, temperature: { label: 'Temp', unit: '°C', min: 36.1, max: 37.8, step: 0.1 }, spo2: { label: 'SpO2', unit: '%', min: 94, max: 100 }, blood_glucose: { label: 'Glucose', unit: 'mmol/L', min: 4.0, max: 7.8, step: 0.1 }, pain_score: { label: 'Pain', unit: '/10', min: 0, max: 10 }, weight: { label: 'Weight', unit: 'kg', min: 20, max: 250, step: 0.1 } };

const VITAL_COLORS = {
  systolic_bp: '#FF6F61', diastolic_bp: '#FF9F89', pulse: '#00A6A0',
  temperature: '#FCD34D', spo2: '#60A5FA', blood_glucose: '#C084FC',
  pain_score: '#F87171', weight: '#34D399',
};

function VitalsTrending({ trends }) {
  const { chart_data, vital_keys, normal_ranges } = trends;
  if (!chart_data || chart_data.length < 2) return null;

  // Group vitals into logical chart panels
  const panels = [
    { keys: ['systolic_bp', 'diastolic_bp'], label: 'Blood Pressure (mmHg)' },
    { keys: ['pulse'], label: 'Pulse (bpm)' },
    { keys: ['temperature'], label: 'Temperature (°C)' },
    { keys: ['spo2'], label: 'SpO2 (%)' },
    { keys: ['blood_glucose'], label: 'Blood Glucose (mmol/L)' },
    { keys: ['pain_score'], label: 'Pain Score (/10)' },
    { keys: ['weight'], label: 'Weight (kg)' },
  ].filter(p => p.keys.some(k => vital_keys.includes(k)));

  if (panels.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="vitals-trending">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-[#00A6A0]" />
        <h3 className="text-sm font-medium text-foreground">Vitals Trend — Last {chart_data.length} observations</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {panels.map(panel => {
          const activeKeys = panel.keys.filter(k => vital_keys.includes(k));
          if (activeKeys.length === 0) return null;
          return (
            <Card key={panel.label} className="glass-card border-border" data-testid={`trend-${activeKeys[0]}`}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-2 font-medium">{panel.label}</p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={chart_data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={d => d?.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: '#888' }} domain={['auto', 'auto']} />
                    <Tooltip contentStyle={{ background: '#1A2329', border: '1px solid #333', borderRadius: '8px', fontSize: '12px' }}
                      labelStyle={{ color: '#999' }} />
                    {activeKeys.map(key => {
                      const nr = normal_ranges[key];
                      return [
                        <Line key={key} type="monotone" dataKey={key} stroke={VITAL_COLORS[key] || '#00A6A0'}
                          strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                          name={RANGES[key]?.label || key} />,
                        nr && <ReferenceLine key={`${key}-min`} y={nr.min} stroke={VITAL_COLORS[key] || '#00A6A0'}
                          strokeDasharray="4 4" strokeOpacity={0.3} />,
                        nr && <ReferenceLine key={`${key}-max`} y={nr.max} stroke={VITAL_COLORS[key] || '#00A6A0'}
                          strokeDasharray="4 4" strokeOpacity={0.3} />,
                      ];
                    })}
                    {activeKeys.length > 1 && <Legend wrapperStyle={{ fontSize: '10px' }} />}
                  </LineChart>
                </ResponsiveContainer>
                {activeKeys.map(key => {
                  const nr = normal_ranges[key];
                  return nr ? (
                    <p key={key} className="text-[10px] text-muted-foreground mt-1">
                      Normal range: {nr.min}–{nr.max} {nr.unit}
                      <span className="inline-block w-8 h-0.5 ml-2 align-middle" style={{ background: VITAL_COLORS[key], opacity: 0.3 }}></span> dashed lines
                    </p>
                  ) : null;
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function HealthQuality() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user?.role === 'caregiver' ? 'observations' : 'kpis');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  const [kpis, setKpis] = useState(null);
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [observations, setObservations] = useState([]);
  const [obsDialog, setObsDialog] = useState(false);
  const [readings, setReadings] = useState({});
  const [trends, setTrends] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { fetchClients(); }, []);
  useEffect(() => { if (isAdmin && tab === 'kpis') fetchKPIs(); }, [tab, period]);
  useEffect(() => { if (selectedClient && tab === 'observations') { fetchObs(); fetchTrends(); } }, [selectedClient, tab]);

  const fetchClients = async () => { try { const { data } = await axios.get(`${API}/api/clinical/my-clients`, { withCredentials: true }); setClients(Array.isArray(data) ? data : []); } catch { setClients([]); } };
  const fetchKPIs = async () => { try { const { data } = await axios.get(`${API}/api/health/quality-kpis?period=${period}`, { withCredentials: true }); setKpis(data); } catch { /* ignore */ } };
  const fetchObs = async () => { try { const { data } = await axios.get(`${API}/api/health/observations/${selectedClient}`, { withCredentials: true }); setObservations(data); } catch { setObservations([]); } };
  const fetchTrends = async () => { try { const { data } = await axios.get(`${API}/api/health/observations/${selectedClient}/trends`, { withCredentials: true }); setTrends(data); } catch { setTrends(null); } };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/api/compliance-export/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `CQC_Compliance_Report.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CQC report downloaded');
    } catch { toast.error('Failed to generate report'); }
    finally { setDownloading(false); }
  };

  const saveObservation = async () => {
    const filled = Object.fromEntries(Object.entries(readings).filter(([,v]) => v !== '' && v != null));
    if (Object.keys(filled).length === 0) return toast.error('Enter at least one reading');
    try {
      const numReadings = Object.fromEntries(Object.entries(filled).map(([k,v]) => [k, parseFloat(v)]));
      const { data } = await axios.post(`${API}/api/health/observations`, { client_id: selectedClient, readings: numReadings }, { withCredentials: true });
      if (data.alerts?.length > 0) { data.alerts.forEach(a => toast.error(`${a.observation}: ${a.value}${a.unit} — outside normal range (${a.normal_range})`, { duration: 6000 })); }
      else toast.success('Observations recorded');
      setObsDialog(false); setReadings({}); fetchObs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const scoreColor = (s) => s >= 80 ? 'text-[#A8E6CF]' : s >= 60 ? 'text-amber-400' : 'text-red-400';
  const scoreBg = (s) => s >= 80 ? 'bg-[#A8E6CF]' : s >= 60 ? 'bg-amber-400' : 'bg-red-400';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="health-quality-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2"><Activity className="w-6 h-6 text-[#00A6A0]" />Health &amp; Quality</h1>
            <p className="text-sm text-muted-foreground mt-1">Vital signs, observations, CQC readiness KPIs</p>
          </div>
          {tab === 'observations' && (
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="bg-muted border-border text-foreground w-full sm:w-56" data-testid="hq-client-select"><SelectValue placeholder="Select client..." /></SelectTrigger>
              <SelectContent className="bg-background border-border">{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {tab === 'kpis' && isAdmin && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-border text-foreground" onClick={downloadReport} disabled={downloading} data-testid="download-cqc-report">
                <Download className="w-4 h-4 mr-1" />{downloading ? 'Generating...' : 'CQC Report PDF'}
              </Button>
              <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="bg-muted border-border text-foreground w-40" data-testid="kpi-period" />
            </div>
          )}
        </div>

        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          {isAdmin && <button onClick={() => setTab('kpis')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'kpis' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-kpis">Quality KPIs</button>}
          <button onClick={() => setTab('observations')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'observations' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-observations">Health Observations</button>
        </div>

        {/* ====== KPI DASHBOARD ====== */}
        {tab === 'kpis' && isAdmin && kpis && (
          <div className="space-y-6" data-testid="kpi-section">
            {/* CQC Readiness Score */}
            <Card className="glass-card border-border" data-testid="cqc-readiness-card">
              <CardContent className="p-6 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">CQC Inspection Readiness Score</p>
                <p className={`text-5xl font-bold ${scoreColor(kpis.cqc_readiness_score)}`}>{kpis.cqc_readiness_score}%</p>
                <div className="mt-3 h-3 rounded-full bg-muted overflow-hidden max-w-md mx-auto">
                  <div className={`h-full rounded-full transition-all ${scoreBg(kpis.cqc_readiness_score)}`} style={{ width: `${kpis.cqc_readiness_score}%` }} />
                </div>
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {Object.entries(kpis.component_scores || {}).map(([k, v]) => (
                    <div key={k} className="text-center">
                      <p className={`text-lg font-bold ${scoreColor(v)}`}>{v}%</p>
                      <p className="text-[10px] text-muted-foreground">{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* KPI Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#A8E6CF]">{kpis.visits?.completion_rate}%</p>
                <p className="text-[10px] text-muted-foreground">Visit Completion</p>
                <p className="text-[10px] text-muted-foreground">{kpis.visits?.completed}/{kpis.visits?.total}</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-400">{kpis.visits?.missed_rate}%</p>
                <p className="text-[10px] text-muted-foreground">Missed Visit Rate</p>
                <p className="text-[10px] text-muted-foreground">{kpis.visits?.missed} missed</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{kpis.medication?.error_rate}%</p>
                <p className="text-[10px] text-muted-foreground">Med Error Rate</p>
                <p className="text-[10px] text-muted-foreground">{kpis.medication?.errors}/{kpis.medication?.total_administrations}</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-[#00A6A0]">{kpis.training?.compliance_rate}%</p>
                <p className="text-[10px] text-muted-foreground">Training Compliance</p>
                <p className="text-[10px] text-muted-foreground">{kpis.training?.signoffs}/{kpis.training?.expected}</p>
              </CardContent></Card>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{kpis.complaints?.total}</p>
                <p className="text-[10px] text-muted-foreground">Complaints</p>
                {kpis.complaints?.overdue > 0 && <p className="text-[10px] text-red-400">{kpis.complaints?.overdue} overdue</p>}
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{kpis.safeguarding?.total}</p>
                <p className="text-[10px] text-muted-foreground">Safeguarding</p>
                {kpis.safeguarding?.open > 0 && <p className="text-[10px] text-amber-400">{kpis.safeguarding?.open} open</p>}
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{kpis.incidents?.total}</p>
                <p className="text-[10px] text-muted-foreground">Incidents</p>
              </CardContent></Card>
              <Card className="glass-card border-border"><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{(kpis.overdue?.care_plan_reviews || 0) + (kpis.overdue?.expired_staff_docs || 0)}</p>
                <p className="text-[10px] text-muted-foreground">Overdue Items</p>
              </CardContent></Card>
            </div>
          </div>
        )}

        {/* ====== HEALTH OBSERVATIONS ====== */}
        {tab === 'observations' && (
          <div className="space-y-4" data-testid="obs-section">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Vital signs and clinical observations — DSCR MODS compliant</p>
              {selectedClient && <Button size="sm" className="btn-primary" onClick={() => { setObsDialog(true); setReadings({}); }} data-testid="new-obs-btn"><Plus className="w-4 h-4 mr-1" />Record Observations</Button>}
            </div>
            {!selectedClient ? (
              <div className="text-center py-12"><User className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">Select a client</p></div>
            ) : (
              <>
                {/* Vitals Trending Charts */}
                {trends && trends.chart_data?.length >= 2 && (
                  <VitalsTrending trends={trends} />
                )}

                {observations.length === 0 ? (
                  <div className="text-center py-8"><Heart className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" /><p className="text-muted-foreground">No observations recorded</p></div>
                ) : (
              <div className="space-y-3">
                {observations.map(obs => (
                  <Card key={obs.id} className={`glass-card border-border ${obs.has_alerts ? 'border-red-500/30' : ''}`} data-testid={`obs-${obs.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-muted-foreground">{obs.observation_date} — by {obs.recorded_by_name}</p>
                        {obs.has_alerts && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">ALERTS</span>}
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {Object.entries(obs.readings || {}).map(([key, val]) => {
                          const r = RANGES[key];
                          if (!r || val == null) return null;
                          const isAbnormal = obs.alerts?.some(a => a.observation === key);
                          return (
                            <div key={key} className={`p-2 rounded-lg text-center ${isAbnormal ? 'bg-red-500/10 border border-red-500/30' : 'bg-muted border border-border'}`}>
                              <p className={`text-sm font-bold ${isAbnormal ? 'text-red-400' : 'text-foreground'}`}>{val}{r.unit}</p>
                              <p className="text-[10px] text-muted-foreground">{r.label}</p>
                            </div>
                          );
                        })}
                      </div>
                      {obs.notes && <p className="text-xs text-muted-foreground mt-2">{obs.notes}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Record Observations Dialog */}
      <Dialog open={obsDialog} onOpenChange={setObsDialog}>
        <DialogContent className="bg-background border-border max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-foreground">Record Health Observations</DialogTitle><DialogDescription>Enter vital sign readings for the selected client.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Enter available readings. Out-of-range values will trigger alerts.</p>
            {Object.entries(RANGES).map(([key, r]) => (
              <div key={key} className="flex items-center gap-3">
                <label className="text-xs text-muted-foreground w-24 text-right">{r.label}</label>
                <Input type="number" step={r.step || 1} value={readings[key] || ''} onChange={e => setReadings(rd => ({ ...rd, [key]: e.target.value }))}
                  placeholder={`${r.min}–${r.max} ${r.unit}`} className="bg-muted border-border text-foreground flex-1" data-testid={`obs-${key}`} />
                <span className="text-[10px] text-muted-foreground w-14">{r.unit}</span>
              </div>
            ))}
            <Button className="w-full btn-primary" onClick={saveObservation} data-testid="save-obs-btn">Save Observations</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
